import {Component} from '@angular/core';
import {ActionsSubject, Store} from '@ngrx/store';
import {QueryService} from '@pt/api-client';
import {forkJoin} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {UiWidget, WidgetConfiguration, WidgetValidator} from '@pt/pt-models';
import {AbstractWidgetDirective} from '../../abstract-widget.directive';
import {WidgetTreatmentDataInterestedV0CellValue} from './models/widget-treatment-data-interested-v0-cell-value';
import {WidgetTreatmentDataInterestedV0Utils} from './widget-treatment-data-interested-v0.utils';
import {WidgetTreatmentDataInterestedPreviewDataV0} from './models/widget-treatment-data-interested-preview-data-v0';
import {WidgetTreatmentDataInterestedV0Response} from './models/widget-treatment-data-interested-v0-response';
import {WidgetTreatmentDataInterestedV0Args} from './models/widget-treatment-data-interested-v0-args';
import {TranslateService} from '@ngx-translate/core';
import {WidgetDialogService} from '../../../services/widget-dialog.service';
import {WidgetTreatmentDataInterestedV0Value} from './models/widget-treatment-data-interested-v0-value';
import {WidgetTreatmentDataInterestedV0Validator} from './models/widget-treatment-data-interested-v0-validator';
import {NgxPermissionsService} from 'ngx-permissions';

@Component({
  selector: 'pt-widget-treatment-data-interested-v0',
  template: `
    <div class='position-relative' *ngIf="internalStatus.enabled">

      <pt-widget-disabled
        *ngIf='internalStatus.isDisabled'
        [showLoader]="internalStatus.viewMode === 'LOADING'">
      </pt-widget-disabled>

      <pt-widget-header
        class='w-100'
        [label]='widget?.label'
        [help]='widget?.help'

        [comment]="comment"
        [unsavedComment]="unsavedComment"
        [showCancelButton]='internalStatus.showCancelButton'
        [showCommentButton]='internalStatus.showCommentButton'
        [showEditButton]='internalStatus.showEditButton'
        [showReloadButton]='internalStatus.showReloadButton'
        [showSaveButton]='internalStatus.showSaveButton'
        [showOptButton]='internalStatus.showOptButton'

        [disabledCancelButton]='internalStatus.disabledCancelButton'
        [disabledCommentButton]='internalStatus.disabledCommentButton'
        [disabledEditButton]='internalStatus.disabledEditButton'
        [disabledOptButton]='internalStatus.disabledOptButton'
        [disabledReloadButton]='internalStatus.disabledReloadButton'
        [disabledSaveButton]='internalStatus.disabledSaveButton'
        [loading]='internalStatus.isLoading'

        (clickSaveWidget)='saveWidget()'
        (clickResetWidget)='resetWidget(widget)'
        (clickEditWidget)='dispatchEditState()'
        (clickReloadWidget)='reloadWidget(widget)'
        (clickCreateOpt)='createOptWidget()'
        (clickCommentWidget)='clickCommentWidget()'>
      </pt-widget-header>

      <ng-container *ngIf='!internalStatus.showOptButton'>
        <pt-widget-treatment-data-interested-v0-edit
          [show]="internalStatus.viewMode === 'EDIT' || internalStatus.viewMode === 'LOADING'"
          [editableData]='editableData'
          (changeValue)='setChangedValue($event)'
        ></pt-widget-treatment-data-interested-v0-edit>

        <pt-widget-treatment-data-interested-v0-preview
          *ngIf="internalStatus.viewMode === 'PREVIEW'"
          [previewData]='previewData'
          [loading]='internalStatus.isLoading'>
        </pt-widget-treatment-data-interested-v0-preview>

        <pt-widget-error
          *ngIf="internalStatus.viewMode === 'ERROR'"
          [errorMessage]='internalStatus.errorMessage | async'>
        </pt-widget-error>

        <pt-widget-footer></pt-widget-footer>
      </ng-container>
    </div>

  `
})
export class WidgetTreatmentDataInterestedV0Component extends AbstractWidgetDirective<WidgetTreatmentDataInterestedPreviewDataV0> {

  public editableData: WidgetTreatmentDataInterestedPreviewDataV0;

  private colTitle: string;
  private colCheckbox: WidgetTreatmentDataInterestedV0Response[];
  private col: WidgetTreatmentDataInterestedV0Response[] = [];
  private colValidator: WidgetValidator[];

  private rowTitle: string;
  private rowCheckbox: WidgetTreatmentDataInterestedV0Response[];
  private row: WidgetTreatmentDataInterestedV0Response[] = [];
  private rowValidator: WidgetValidator[];

  private cells: WidgetTreatmentDataInterestedV0CellValue[] = [];

  private widgetValue: WidgetTreatmentDataInterestedV0Value;

  constructor(public store: Store,
              public actionsSubject: ActionsSubject,
              public translateService: TranslateService,
              public widgetDialogService: WidgetDialogService,
              public service: QueryService,
              public permissionService: NgxPermissionsService) {
    super(store, actionsSubject, translateService, widgetDialogService, permissionService);
  }

  beforeOnInit(): void {
    this.loadingWidgetValue();
  }

  loadWidget(widget: UiWidget): void {
    this.dispatchInitState();
    this.row = [];
    this.col = [];
    this.cells = [];
    this.previewData = null;
    this.editableData = null;
    const queryCol = (this.initializationWidget?.args as WidgetTreatmentDataInterestedV0Args)?.queryCol;
    const queryRow = (this.initializationWidget?.args as WidgetTreatmentDataInterestedV0Args)?.queryRow;
    const args = widget.args as WidgetTreatmentDataInterestedV0Args;

    this.colTitle = args.header.col;
    this.rowTitle = args.header.row;

    if (queryCol && queryRow) {
      forkJoin({
        col: this.service.resolve(queryCol),
        row: this.service.resolve(queryRow)
      }).pipe(
        takeUntil(this.destroy$)
      ).subscribe(
        (responses: { col, row }) => {
          this.widgetValue = widget.value as WidgetTreatmentDataInterestedV0Value;
          const validators = widget.validators as WidgetTreatmentDataInterestedV0Validator;
          this.colValidator = validators.col;
          this.rowValidator = validators.row;
          this.colCheckbox = WidgetTreatmentDataInterestedV0Utils.queryResponseToCheckBoxResponse(responses.col, queryCol.mapping, this.widgetValue?.col);
          this.rowCheckbox = WidgetTreatmentDataInterestedV0Utils.queryResponseToCheckBoxResponse(responses.row, queryRow.mapping, this.widgetValue?.row);

          this.populateTableRow(this.widgetValue?.row);
          this.populateTableCol(this.widgetValue?.col);
          this.populateTableCells(this.widgetValue?.cell);

          this.editableData = {
            colTitle: this.colTitle,
            colCheckbox: this.colCheckbox,
            colValidator: this.colValidator,
            colGroupByCategory: args?.queryCol?.groupForCategory,

            rowTitle: this.rowTitle,
            rowCheckbox: this.rowCheckbox,
            rowValidator: this.rowValidator,
            rowGroupByCategory: args?.rowGroupForCategory,

            row: this.row,
            col: this.col,
            cells: this.cells
          } as WidgetTreatmentDataInterestedPreviewDataV0;

          this.valueController(widget, this.editableData);

          this.checkValidity();

          setTimeout(() => this.dispatchCompleteState(this.editableData));
        },
        () => {
          this.dispatchErrorState();
        }
      );
    } else {
      this.dispatchErrorState();
    }
  }

  saveWidget(ignoreTrigger?: boolean): void {
    const widget = JSON.parse(JSON.stringify(this.initializationWidget)) as UiWidget;
    widget.value = WidgetTreatmentDataInterestedV0Utils.createValue(
      this.initializationWidget?.value as WidgetTreatmentDataInterestedV0Value,
      this.row,
      this.col,
      this.cells
    );
    widget.comment = this.comment;
    this.checkValidity();
    this.completeSaveWidget(widget, ignoreTrigger);
  }

  public isNoData(): boolean {
    const value = this.widget.value as WidgetTreatmentDataInterestedV0Value;
    return value === null || value === undefined ||
      (value?.col?.length === 0 && value?.row?.length === 0 && value?.cell?.length === 0);
  }

  configuration(): WidgetConfiguration {
    return {supportAutoSave: false, supportValidation: false};
  }

  setChangedValue(value: WidgetTreatmentDataInterestedV0Value) {
    this.widgetValue = value;
    this.populateTableRow(this.widgetValue.row);
    this.populateTableCol(this.widgetValue.col);
    this.populateTableCells(this.widgetValue.cell);
    this.checkValidity();
  }

  private loadingWidgetValue() {
    this.widgetValue = this.initializationWidget?.value;
  }

  private populateTableRow(row: string[]) {
    this.row = [];
    if (row?.length > 0 && this.rowCheckbox?.length > 0) {
      this.rowCheckbox.forEach((checkbox) => {
        if (row.includes(checkbox.uuid)) {
          this.row.push({
            uuid: checkbox.uuid,
            label: checkbox.label,
            help: checkbox.help,
            category: checkbox.category
          });
        }
      });
    }
    this.checkValidity();
  }

  private populateTableCol(col: string[]) {
    this.col = [];
    if (col?.length > 0 && this.colCheckbox?.length > 0) {
      this.colCheckbox.forEach((checkbox) => {
        if (col.includes(checkbox.uuid)) {
          this.col.push({
            uuid: checkbox.uuid,
            label: checkbox.label,
            help: checkbox.help,
            category: checkbox.category
          });
        }
      });
    }
    this.checkValidity();
  }

  private populateTableCells(cells: WidgetTreatmentDataInterestedV0CellValue[]) {
    this.cells = cells ? JSON.parse(JSON.stringify(cells)) : null;
    this.checkValidity();
  }

  private checkValidity() {
    if (this.widget?.opt) {
      this.isValid$.next(true);
    } else {
      this.isValid$.next(this.cells?.length > 0);
    }
  }

  private valueController(widget: UiWidget, data: WidgetTreatmentDataInterestedPreviewDataV0) {

    const noData = !data || data?.colCheckbox?.length === 0 || data?.rowCheckbox?.length === 0;
    if (noData) {
      this.sendWidgetNotificationInvalidQueryResponseNoData(data);
    }

    const widgetValue: WidgetTreatmentDataInterestedV0Value = widget?.value
      ?
      widget?.value
      :
      {
        col: [],
        row: [],
        cell: []
      };
    const validRow = data?.rowCheckbox?.length > 0 ? data.rowCheckbox.map((r) => r.uuid) : [];
    const validCol = data?.colCheckbox?.length > 0 ? data.colCheckbox.map((c) => c.uuid) : [];
    const validValue: WidgetTreatmentDataInterestedV0Value = noData
      ?
      {
        col: [],
        row: [],
        cell: []
      }
      :
      WidgetTreatmentDataInterestedV0Utils.getValidValue(widgetValue, validRow, validCol);

    if (validValue?.col?.length !== widgetValue?.col?.length ||
      validValue?.row?.length !== widgetValue?.row?.length ||
      validValue?.cell?.length !== widgetValue?.cell?.length) {
      const updatedWidget = JSON.parse(JSON.stringify(widget));
      updatedWidget.value = validValue;
      this.sendWidgetNotificationInvalidQueryResponseInvalidValue(updatedWidget);
    }
  }
}









